import { format } from 'date-fns/format';
import { fromUnixTime } from 'date-fns/fromUnixTime';

const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const DATE_TIME_MS_FORMAT = 'yyyy-MM-dd HH:mm:ss.SSS'
const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * @param {number|string} value UNIX timestamp in seconds or milliseconds
 * @returns {string}
 */
export function toDateTime(value) {
  if (typeof value !== 'number' && typeof value !== 'string') {
    throw new Error('value parameter must be a valid Unix timestamp.');
  }

  const timestamp = Number(value);

  if (isNaN(timestamp)) {
    throw new Error("Invalid timestamp format.");
  }

  const formatDate = new Date(
    // Check if timestamp is in milliseconds or seconds and convert it to milliseconds as necessary.
    // Remark (Tosh): timestamps in milliseconds could be shorter than 13 characters if the value is
    // before 999999999999 (2001-09-09T01:46:39.999Z) expressed as milliseconds.
    timestamp.toString().length === 13
      ? timestamp
      : fromUnixTime(timestamp),
  )

  return format(formatDate, DATE_FORMAT);
}

/**
 * @param {string} value
 * @returns {string}
 */
export function isoToDatetime(value) {
  if (typeof value !== 'string' && !(value instanceof Date)) {
    console.error('The value must be a timestamp or a Date object');
    return '';
  }

  return format(value, DATE_TIME_FORMAT);
}

/**
 *
 * @param {string|number} value
 * @returns {string}
 */
export function isoToDate(value) {
  if (typeof value === 'number') {
    return format(fromUnixTime(value), DATE_FORMAT);
  }

  if (typeof value !== 'string') {
    throw 'value parameter must be datetime string or timestamp.';
  }

  return format(value, DATE_FORMAT);
}

/**
 * Formats a Date value accordingly in the default format, specified by *DATE_FORMAT*.
 *
 * @param {Date} value
 * @returns {string}
 */
export function formatDate(value) {
  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return format(value, DATE_FORMAT);
  } else if (typeof value === 'string' || typeof value === 'number') {
    const asDate = new Date(value);
    if (!Number.isNaN(asDate)) {
      return format(asDate, DATE_FORMAT);
    }
  }

  throw new TypeError('value must be a Date object, a valid date string or a number that could be converted to date.');
}

/**
 * Formats a Date value accordingly in the default format, specified by *DATE_TIME_FORMAT*.
 *
 * @param {Date} value
 * @returns {string}
 */
export function formatDateTime(value) {
  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return format(value, DATE_TIME_FORMAT);
  }

  throw new TypeError('value must be a Date object');
}

/**
 * Formats a Date value accordingly in the default format, specified by *DATE_TIME_MS_FORMAT*.
 *
 * @param {Date} value
 * @returns {string}
 */
export function formatDateTimeMilliseconds(value) {
  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return format(value, DATE_TIME_MS_FORMAT);
  }

  throw new TypeError('value must be a Date object');
}
